import React, { useCallback, useEffect, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, TextField, Typography } from '@mui/material'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import CloseIcon from '@mui/icons-material/Close'
import Swal from 'sweetalert2'
import { CentroCard } from '../components/centros/CentroCard'
import { deleteAlmacen, getAlmacen, getAlmacenes, patchAlmacen, postAlmacen } from '../services/apis'
import { LoadingMaestro } from '../components/LoadingMaestro'
import { normalizarArray } from '../utils/apiResponse'

const getApiErrorMessage = (error, fallback) => {
    const message = error.response?.data?.message

    if (Array.isArray(message)) {
        return message.join('\n')
    }

    return message || fallback
}

export const Centros = () => {
    const [almacenes, setAlmacenes] = useState([])
    const [loading, setLoading] = useState(true)
    const [openAlmacen, setOpenAlmacen] = useState(false)
    const [almacenEditandoCod, setAlmacenEditandoCod] = useState(null)
    const [almacenEliminar, setAlmacenEliminar] = useState(null)
    const [almacenForm, setAlmacenForm] = useState({
        codAlmacen: '',
        desAlmacen: '',
    })

    const cargarAlmacenes = useCallback(async () => {
        setLoading(true)
        try {
            const response = await getAlmacenes()
            const almacenesData = normalizarArray(response, 'almacenes')

            setAlmacenes(almacenesData)
        } catch (error) {
            console.error('Error al cargar los almacenes:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al cargar los almacenes'),
                timer: 2000,
                showConfirmButton: false,
            })
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        cargarAlmacenes()
    }, [cargarAlmacenes])

    const resetAlmacenForm = () => {
        setAlmacenForm({
            codAlmacen: '',
            desAlmacen: '',
        })
    }

    const handleChangeAlmacen = (event) => {
        const { name, value } = event.target

        setAlmacenForm((almacenActual) => ({
            ...almacenActual,
            [name]: value,
        }))
    }

    const abrirCrearAlmacen = () => {
        resetAlmacenForm()
        setAlmacenEditandoCod(null)
        setOpenAlmacen(true)
    }

    const cerrarAlmacen = () => {
        setOpenAlmacen(false)
        setAlmacenEditandoCod(null)
        resetAlmacenForm()
    }

    const abrirEditarAlmacen = async (codAlmacen) => {
        try {
            const response = await getAlmacen(codAlmacen)
            const almacen = response?.data ?? response

            setAlmacenForm({
                codAlmacen: almacen.codAlmacen ?? '',
                desAlmacen: almacen.desAlmacen ?? '',
            })
            setAlmacenEditandoCod(codAlmacen)
            setOpenAlmacen(true)
        } catch (error) {
            console.error('Error al cargar el almacen:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al cargar el almacen'),
                timer: 2000,
                showConfirmButton: false,
            })
        }
    }

    const guardarAlmacen = async (event) => {
        event.preventDefault()

        const almacenEnvio = {
            codAlmacen: almacenForm.codAlmacen.trim(),
            desAlmacen: almacenForm.desAlmacen.trim(),
        }

        try {
            if (almacenEditandoCod) {
                await patchAlmacen(almacenEditandoCod, almacenEnvio)
            } else {
                await postAlmacen(almacenEnvio)
            }

            cerrarAlmacen()
            cargarAlmacenes()
            Swal.fire({
                icon: 'success',
                title: almacenEditandoCod ? 'Almacen actualizado' : 'Almacen creado',
                showConfirmButton: false,
                timer: 1500,
            })
        } catch (error) {
            console.error('Error al guardar el almacen:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al guardar el almacen'),
                timer: 2500,
                showConfirmButton: false,
            })
        }
    }

    const confirmarEliminarAlmacen = async () => {
        if (!almacenEliminar) return

        try {
            await deleteAlmacen(almacenEliminar.codAlmacen)
            setAlmacenEliminar(null)
            cargarAlmacenes()
            Swal.fire({
                icon: 'success',
                title: 'Almacen eliminado',
                showConfirmButton: false,
                timer: 1500,
            })
        } catch (error) {
            console.error('Error al eliminar el almacen:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al eliminar el almacen'),
                timer: 2500,
                showConfirmButton: false,
            })
        }
    }

    return (
        <Grid
            container
            direction="row"
            size={{ xs: 12 }}
            sx={{
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <Grid
                container
                direction="row"
                size={{ xs: 10 }}
                sx={{
                    justifyContent: "start",
                    alignItems: "start",
                }}
                className='fade-in ajustes-compact'
            >
                {loading ? (
                    <LoadingMaestro label="Cargando almacenes..." />
                ) : (
                <>
                <Grid
                    container
                    direction="row"
                    size={{ xs: 12 }}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        my: 2,
                    }}
                >
                    <Grid container direction="column" size={{ xs: 12, sm: 7 }} sx={{ alignItems: "start" }}>
                        <Typography variant='h4'>
                            Almacenes
                        </Typography>
                    </Grid>

                    <Grid container direction="row" size={{ xs: 12, sm: 4, md: 3, lg: 2 }} sx={{ justifyContent: { xs: "start", sm: "end" } }}>
                        <Button variant="contained" fullWidth onClick={abrirCrearAlmacen}>
                            <AddCircleIcon />&nbsp;Nuevo almacen
                        </Button>
                    </Grid>

                    <Divider />
                </Grid>

                <Grid
                    container
                    direction="row"
                    size={{ xs: 12 }}
                    sx={{
                        justifyContent: "start",
                        alignItems: "stretch",
                    }}
                    spacing={2}
                >
                    {almacenes.map((info, index) => (
                        <Grid key={info.codAlmacen ?? index} container size={{ xs: 12, md: 6, lg: 3 }}>
                            <CentroCard info={info} onEdit={abrirEditarAlmacen} onDelete={setAlmacenEliminar} />
                        </Grid>
                    ))}
                </Grid>
                </>
                )}
            </Grid>

            <Dialog open={openAlmacen} onClose={cerrarAlmacen} fullWidth maxWidth="sm">
                <form onSubmit={guardarAlmacen}>
                    <DialogTitle>
                        <Grid
                            container
                            direction="row"
                            size={{ xs: 12 }}
                            sx={{
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="h5">
                                {almacenEditandoCod ? 'Editar almacen' : 'Nuevo almacen'}
                            </Typography>

                            <Button variant="text" color="negro" onClick={cerrarAlmacen}>
                                <CloseIcon />
                            </Button>
                        </Grid>
                    </DialogTitle>

                    <DialogContent>
                        <Grid
                            container
                            direction="row"
                            size={{ xs: 12 }}
                            sx={{
                                justifyContent: "center",
                                alignItems: "center",
                                pt: 1,
                            }}
                            spacing={2}
                        >
                            <Grid container size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    label="Codigo"
                                    name="codAlmacen"
                                    value={almacenForm.codAlmacen}
                                    onChange={handleChangeAlmacen}
                                    fullWidth
                                    required
                                    disabled={Boolean(almacenEditandoCod)}
                                />
                            </Grid>

                            <Grid container size={{ xs: 12, sm: 8 }}>
                                <TextField
                                    label="Descripcion"
                                    name="desAlmacen"
                                    value={almacenForm.desAlmacen}
                                    onChange={handleChangeAlmacen}
                                    fullWidth
                                    required
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        <Button color="primary" onClick={cerrarAlmacen}>
                            Cancelar
                        </Button>

                        <Button color="primary" variant="contained" type="submit">
                            {almacenEditandoCod ? 'Guardar almacen' : 'Crear almacen'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Dialog open={Boolean(almacenEliminar)} onClose={() => setAlmacenEliminar(null)} fullWidth maxWidth="xs">
                <DialogTitle>
                    Eliminar almacen
                </DialogTitle>

                <DialogContent>
                    <Typography>
                        Desea eliminar el almacen {almacenEliminar?.desAlmacen || ''}?
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button color="primary" onClick={() => setAlmacenEliminar(null)}>
                        Cancelar
                    </Button>

                    <Button color="error" variant="contained" onClick={confirmarEliminarAlmacen}>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}
